import { getSnowdonJwt } from '~/domains/auth/utils';
import { logError } from '~/utils/datadog';
import { ENV } from '~/utils/env';
import { isClient, isServer } from '~/utils/ssr';
import { INTERNAL_MARKET_API_URL, MARKET_API_URL } from '~/utils/url';

import { HttpError } from './http-error';
import type { HttpClientConfig, RequestOptions, ResponseInfo } from './types';

function getAuthCookieName(): string {
  switch (ENV) {
    case 'prod':
    case 'stg':
      return 'snowdon_production_jwt';
    case 'dev':
    default:
      return 'snowdon_dev_jwt';
  }
}

function getServerAuthCookie(): string {
  if (!isServer()) return '';

  try {
    // next/headers는 App Router Server Component에서만 사용 가능
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require, @typescript-eslint/no-require-imports
    const nextHeaders = require('next/headers');
    const cookieName = getAuthCookieName();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const authCookie = nextHeaders.cookies().get(cookieName) as
      | { name: string; value: string }
      | undefined;

    if (!authCookie) return '';
    return `${authCookie.name}=${authCookie.value}`;
  } catch {
    // Pages Router 또는 cookies() 사용 불가 환경
    return '';
  }
}

export class HttpClient {
  private baseUrl: string;

  private tokenProvider: () => string | undefined;

  private onResponse?: (info: ResponseInfo) => void;

  constructor(config: HttpClientConfig = {}) {
    this.baseUrl =
      config.baseUrl ?? (isServer() ? INTERNAL_MARKET_API_URL : MARKET_API_URL);
    this.tokenProvider = config.tokenProvider ?? getSnowdonJwt;
    this.onResponse = config.onResponse;
  }

  async request<TResponse, TData = unknown>(
    options: RequestOptions<TData>,
  ): Promise<TResponse> {
    const { url, method, params, data, headers } = options;
    const requestUrl = this.buildUrl(url, params);
    const token = this.tokenProvider();
    const startTime = Date.now();

    // 서버에서는 인증 쿠키를 수동으로 전달
    const serverAuthCookie = isServer() ? getServerAuthCookie() : '';

    // FormData(multipart)인 경우 Content-Type을 직접 설정하지 않고(브라우저가
    // boundary 포함 multipart/form-data 자동 설정), body도 직렬화하지 않는다.
    const isFormData =
      typeof FormData !== 'undefined' && data instanceof FormData;

    const response = await fetch(requestUrl, {
      method: method.toUpperCase(),
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        'X-Market-Front-Page': isClient() ? window.location.href : 'server',
        ...(isClient() && token ? { 'X-Client-Auth-Token': token } : {}),
        ...(serverAuthCookie ? { Cookie: serverAuthCookie } : {}),
        ...headers,
      },
      body: isFormData
        ? (data as FormData)
        : data
        ? JSON.stringify(data)
        : undefined,
      credentials: 'include',
    });

    const duration = Date.now() - startTime;

    this.onResponse?.({
      url: requestUrl,
      method,
      status: response.status,
      duration,
    });

    const responseData = response.headers
      .get('content-type')
      ?.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const error = new HttpError(response.status, responseData, requestUrl);
      logError(error);
      throw error;
    }

    return responseData as TResponse;
  }

  private buildUrl(url: string, params?: Record<string, unknown>): string {
    const fullUrl = `${this.baseUrl}${url}`;
    if (!params) return fullUrl;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const qs = searchParams.toString();
    return qs ? `${fullUrl}?${qs}` : fullUrl;
  }
}

export const httpClient = new HttpClient();
